import { useMutation, useQuery } from '@apollo/client/react';
import React, { useState } from 'react';
import './EditEquipmentDetails.scss';
import { CustomButton, Error, Loading } from '../../../_common';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GET_ASSET_INFO, GET_ASSET_LOADS, GET_EQUIPMENTS_INFO } from '../../../../graphql/client/queries';
import EquipmentDetailsEdit from './EquipmentsDetailsEdit/EquipmentsDetailsEdit';
import { useAuthContext } from '../../../Auth/AuthProvider';
import { PermissionEnum } from '../../../../types/PermissionEnum';
import { Routes } from '../../../../utils';
import {
    GetAssestLoadsByLoadId_getAssestLoadsByLoadId as AssetLoadType,
    GetAssestLoadsByLoadId,
    GetAssetInfoByLoadId_getAssetInfoByLoadId as AssetInfoType,
    GetAssetInfoByLoadId,
    EditAssetLoad,
    AssetInfoType as AssetInfoTypeToSave,
    AssetLoadType as AssetLoadTypeToSave,
    GetEquipmentsInfo,
    GetEquipmentsInfo_getEquipmentsInfo as EquipmentsInfo,
} from '../../../../types/schemaTypes';
import { EDIT_ASSET_LOAD } from '../../../../graphql/client/mutations';
import LoadDriverDetailsEdit from './LoadDriverDetailsEdit';
import { Modal } from 'react-bootstrap';
import { DropdownOption } from '../../../_common/SearchableDropdown/SearchableDropdown';

interface AssetInfoToSaveProps {
    index: number;
    assetTypeId: number | null;
    assetType: string | null;
    assetId: number | null;
    assetDescrip: string | null;
    quantity: number | null;
}

const EditEquipmentDetails = ({ match }: any): React.ReactElement => {
    const [tabTitle, setTabTitle] = useState(document.title);
    const navigate = useNavigate();
    const authContext = useAuthContext();

    const canEditAssetLoads = authContext.userHasPermission(PermissionEnum.EditAssetLoadDetails);
    const isAssignLoadEnabled = authContext.userHasPermission(PermissionEnum.AssignAssetLoads);

    const loadId = parseInt(useParams().loadId ?? '0');
    document.title = loadId ? 'Asset Load# :' + loadId : tabTitle;

    const [assetInfoToSave, setAssetInfoToSave] = useState<AssetInfoToSaveProps[]>();
    const [assetLoadToSave, setAssetLoadToSave] = useState<AssetLoadType>();

    const [assetInfoExists, setAssetInfoExists] = useState(false);
    const [isContinueMode, setIsContinueMode] = useState(false);
    const [validationError, setValidationError] = useState('');

    let assetLoad: AssetLoadType | undefined;
    const assetInfo: AssetInfoType[] = [];
    const { data: assetLoadData, loading: assetLoadLoading, error: assetLoadError } = useQuery<GetAssestLoadsByLoadId>(GET_ASSET_LOADS, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
    });
    const { data: assetInfoData, loading: assetInfoLoading, error: assetInfoError } = useQuery<GetAssetInfoByLoadId>(GET_ASSET_INFO, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
    });

    if (assetLoadData && assetLoadData.getAssestLoadsByLoadId) {
        assetLoad = assetLoadData.getAssestLoadsByLoadId;
        if (!assetLoadToSave) setAssetLoadToSave(assetLoad);
    }
    if (assetInfoData && assetInfoData.getAssetInfoByLoadId) {
        assetInfoData.getAssetInfoByLoadId.map((info: any) => {
            if (info)
                assetInfo.push(info);
        })
    }


    let equipmentsInfo: EquipmentsInfo | undefined;
    // GET_EQUIPMENTS_INFO - New fields to be added in API response
    const { data: equipmentInfoData, loading: equipmentInfoDataLoading, error: equipmentInfoDataError } = useQuery<GetEquipmentsInfo>(GET_EQUIPMENTS_INFO, {
        variables: {
            input: {
                originType: assetLoadToSave?.originType,
                originId: assetLoadToSave?.originType === "Job" ? assetLoadToSave?.originJobId : (assetLoadToSave?.originType === "Lofa" ? assetLoadToSave?.originLofaId : (assetLoadToSave?.originType === "Yard" ? assetLoadToSave?.originYardId : (assetLoadToSave?.originType === "Other" ? 0 : 0)))
            }
        },
        fetchPolicy: "network-only",
        skip: !assetLoadToSave || !assetLoadToSave.originType || (assetLoadToSave.originType !== "Other" && !assetLoadToSave.originJobId && !assetLoadToSave.originLofaId && !assetLoadToSave.originYardId),
    });
    if (equipmentInfoData && equipmentInfoData.getEquipmentsInfo) {
        equipmentsInfo = equipmentInfoData.getEquipmentsInfo;
    }

    const payCodeList: DropdownOption[] = [
        { id: 0, name: "" },
        { id: 1, name: "Forklift (Heavy Haul)" },
        { id: 2, name: "Single Empty Box" },
        { id: 3, name: "Two Empty Boxes" },
        { id: 4, name: "Loaded Box" },
        { id: 5, name: "Cradle" },
        { id: 6, name: "Stepdeck" },
        { id: 7, name: "Loader (Heavy Haul)" },
    ];

    const updatePaycode = () => {
        if (assetLoadToSave) {
            const tempAssetLoadToSave: AssetLoadType = { ...assetLoadToSave };

            // If trailer type is 'Stepdeck' or 'Sandbox Chassis' then paycode is defaulted
            if (['Stepdeck', 'Sandbox Chassis', 'Cradle'].includes(tempAssetLoadToSave.trailerType ?? '')) return;

            let payCodeId = -1;
            assetInfoToSave?.map(info => {
                if (info.assetType === "Forklift") payCodeId = 1;
                if (info.assetType === "Front end loader") payCodeId = 7;
                if (info.assetType === "Cradle") payCodeId = 5;
                if (info.assetType === "Sandbox") (payCodeId === 2 || payCodeId === 3) ? payCodeId = 3 : payCodeId = 2;
            })

            if (payCodeId === -1) tempAssetLoadToSave.payCode = payCodeList[6].name.toString();
            else tempAssetLoadToSave.payCode = payCodeList[payCodeId].name.toString();

            setAssetLoadToSave(tempAssetLoadToSave);
        }
    };


    const [editAssetLoad, { loading: mutationLoading, error: mutationError }] = useMutation<EditAssetLoad>(EDIT_ASSET_LOAD,
        {
            onCompleted: () => {
                navigate(`${Routes.EQUIPMENT_MANAGEMENT.route}${loadId}`);
            }
        }
    );

    const isSaveButtonDisabled = () => {
        let isDisabled = false;
        // Atleast one equipment must be added when state is Assigned
        if (assetLoadToSave?.status === "Assigned" && (!assetInfoToSave || assetInfoToSave.length === 0)) {
            isDisabled = true;
            return isDisabled;
        }
        assetInfoToSave?.map(assetInfo => {
            // Equipment Type must be selected
            if (!assetInfo.assetType) isDisabled = true;
            // If Assigning Driver -- Equipment ID/ Quantity must be added
            if (assetInfo.assetType && assetLoadToSave?.driverName &&
                (
                    (!["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType) && !assetInfo.assetId) ||
                    (["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType) && !assetInfo.quantity)
                )
            )
                isDisabled = true;
        })

        // If Assigning driver -- Trailer Type is mandatory
        if (assetLoadToSave?.driverName && !assetLoadToSave?.trailerTypeId) isDisabled = true;
        // If Assigning driver -- Trailer ID is mandatory
        if (assetLoadToSave?.driverName && !assetLoadToSave?.trailerId) isDisabled = true;

        // If stepdeck -- Miles is Mandatory
        if (assetLoadToSave?.trailerTypeId === 6 && !assetLoadToSave?.miles) isDisabled = true;

        // Origin is Mandatory
        if (assetLoadToSave?.originType === "Job" && !assetLoadToSave.originJobId) isDisabled = true;
        if (assetLoadToSave?.originType === "Lofa" && !assetLoadToSave.originLofaId) isDisabled = true;
        if (assetLoadToSave?.originType === "Yard" && !assetLoadToSave.originYardId) isDisabled = true;

        // Destination is Mandatory
        if (assetLoadToSave?.destinationType === "Job" && !assetLoadToSave.destinationJobId) isDisabled = true;
        if (assetLoadToSave?.destinationType === "Lofa" && !assetLoadToSave.destinationLofaId) isDisabled = true;
        if (assetLoadToSave?.destinationType === "Yard" && !assetLoadToSave.destinationYardId) isDisabled = true;

        // If Latitude is entered then Longitude is Mandatory
        // If Longitude is entered then Latitude is Mandatory
        if (
            assetLoadToSave?.originType === "Other" && (
                (assetLoadToSave.originLatitude && !assetLoadToSave.originLongitude) ||
                (assetLoadToSave.originLongitude && !assetLoadToSave.originLatitude)
            )
        ) isDisabled = true;

        // If Destination type is Other then Latitude and Longitude are Mandatory
        if (
            assetLoadToSave?.destinationType === "Other" && (
                !assetLoadToSave.destinationLongitude || !assetLoadToSave.destinationLatitude
            )
        ) isDisabled = true;

        // Origin Region and District are Mandatory
        if (!assetLoadToSave?.originRegionId || !assetLoadToSave?.originDistrictId) isDisabled = true;
        // Destination Region and District is Mandatory
        if (!assetLoadToSave?.destinationRegionId || !assetLoadToSave?.destinationDistrictId) isDisabled = true;

        // Origin and Destination must be different
        if (assetLoadToSave?.originJobId && assetLoadToSave?.originJobId === assetLoadToSave?.destinationJobId) isDisabled = true;
        if (assetLoadToSave?.originLofaId && assetLoadToSave?.originLofaId === assetLoadToSave?.destinationLofaId) isDisabled = true;
        if (assetLoadToSave?.originYardId && assetLoadToSave?.originYardId === assetLoadToSave?.destinationYardId) isDisabled = true;

        // If load type is 'Transfer' then Origin region and Destination region must be different
        if (assetLoadToSave?.assetLoadType === "Equipment Transfer" && assetLoadToSave.originRegionId === assetLoadToSave.destinationRegionId) isDisabled = true;
        // If load type is 'Move' then Origin region and Destination region must be the same
        if (assetLoadToSave?.assetLoadType === "Equipment Move" && assetLoadToSave.originRegionId !== assetLoadToSave.destinationRegionId) isDisabled = true;

        // Check if asset info exists
        let infoExists = false;
        assetInfoToSave?.map(assetInfo => {
            if (!["Mats", "Forklift Ramp", "Miscellaneous"].includes(assetInfo.assetType ?? '') && assetInfo.assetId)
                infoExists = true;
        })
        if (assetInfoExists !== infoExists) setAssetInfoExists(infoExists);

        // Trailer type must be selected if status is not created or rfd
        if (!["Created", "ReadyForDispatch"].includes(assetLoadToSave?.status ?? '') && !assetLoadToSave?.trailerTypeId) isDisabled = true;

        return isDisabled;
    }

    const handleOnSuccessValidation = () => {
        const currentDetails: AssetInfoTypeToSave[] = [];
        assetInfo.map(info => {
            currentDetails.push({
                assetTypeId: info.assetTypeId,
                assetType: info.assetType,
                quantity: info.quantity,
                assetId: info.assetId,
                assetDescrip: info.assetDescrip,
            })
        })
        const currentValues: AssetLoadTypeToSave = {
            assetLoadTypeId: assetLoad?.assetLoadTypeId,
            originType: assetLoad?.originType,
            originJobId: assetLoad?.originJobId,
            originLofaId: assetLoad?.originLofaId,
            originYardId: assetLoad?.originYardId,
            originLatitude: assetLoad?.originLatitude,
            originLongitude: assetLoad?.originLongitude,
            originRegionId: assetLoad?.originRegionId,
            originDistrictId: assetLoad?.originDistrictId,
            destinationType: assetLoad?.destinationType,
            destinationJobId: assetLoad?.destinationJobId,
            destinationLofaId: assetLoad?.destinationLofaId,
            destinationYardId: assetLoad?.destinationYardId,
            destinationRegionId: assetLoad?.destinationRegionId,
            destinationDistrictId: assetLoad?.destinationDistrictId,
            destinationLatitude: assetLoad?.destinationLatitude,
            destinationLongitude: assetLoad?.destinationLongitude,
            trailerId: assetLoad?.trailerId,
            trailerTypeId: assetLoad?.trailerTypeId,
            truckNumber: assetLoad?.truckNumber,
            driverId: assetLoad?.driverId,
            miles: assetLoad?.miles,
            accessorialCost: assetLoad?.accessorialCost,
            payCode: assetLoad?.payCode,
            details: currentDetails,
        }

        const newDetails: AssetInfoTypeToSave[] = [];
        assetInfoToSave?.map(info => {
            if (info.assetTypeId)
                newDetails.push({
                    assetTypeId: info.assetTypeId,
                    assetType: info.assetType,
                    quantity: info.quantity,
                    assetId: info.assetId,
                    assetDescrip: info.assetDescrip,
                })
        })
        const newValues: AssetLoadTypeToSave = {
            assetLoadTypeId: assetLoadToSave?.assetLoadTypeId,
            originType: assetLoadToSave?.originType,
            originJobId: assetLoadToSave?.originJobId,
            originLofaId: assetLoadToSave?.originLofaId,
            originYardId: assetLoadToSave?.originYardId,
            originLatitude: assetLoadToSave?.originLatitude,
            originLongitude: assetLoadToSave?.originLongitude,
            originRegionId: assetLoadToSave?.originRegionId,
            originDistrictId: assetLoadToSave?.originDistrictId,
            destinationType: assetLoadToSave?.destinationType,
            destinationJobId: assetLoadToSave?.destinationJobId,
            destinationLofaId: assetLoadToSave?.destinationLofaId,
            destinationYardId: assetLoadToSave?.destinationYardId,
            destinationRegionId: assetLoadToSave?.destinationRegionId,
            destinationDistrictId: assetLoadToSave?.destinationDistrictId,
            destinationLatitude: assetLoadToSave?.destinationLatitude,
            destinationLongitude: assetLoadToSave?.destinationLongitude,
            trailerId: assetLoadToSave?.trailerId,
            trailerTypeId: assetLoadToSave?.trailerTypeId,
            truckNumber: assetLoadToSave?.truckNumber,
            driverId: assetLoadToSave?.driverId,
            miles: assetLoadToSave?.miles,
            accessorialCost: assetLoadToSave?.accessorialCost,
            payCode: assetLoadToSave?.payCode,
            details: newDetails,
        }

        editAssetLoad({
            variables: {
                input: {
                    assetLoadId: loadId,
                    currentValues: currentValues,
                    newValues: newValues,
                }
            }
        })
    }

    const handleOnClickSave = () => {
        // Validation - If Mob load then asset type must no be OOS
        setValidationError('');
        const outOfServiceEquipments: string[] = [];
        assetInfoToSave?.map(info => {
            const equimentType = info.assetType?.toLowerCase().replace('-', '').replace(' ', '');
            if (equimentType && equipmentsInfo)
                equipmentsInfo[equimentType]?.map(equipment => {
                    if (info.assetId === equipment.itemId && equipment.status === "Out of Service")
                        outOfServiceEquipments.push(equipment.descrip);
                })
        })
        if (outOfServiceEquipments.length > 0) {
            let errorMessage = 'Equipments - ';
            if (outOfServiceEquipments.length === 1) {
                errorMessage = `Equipment - ${outOfServiceEquipments[0]} is Out of Service`;
            } else {
                outOfServiceEquipments.map(outOfServiceEquipment => {
                    errorMessage += `${outOfServiceEquipment}, `
                })
                errorMessage += 'are Out of Service';
            }
            setValidationError(errorMessage);

            if (assetLoadToSave?.assetLoadType !== "Mobilization Load")
                setIsContinueMode(true);
            return;
        }
        else {
            handleOnSuccessValidation();
        }
    }

    if (!canEditAssetLoads && !isAssignLoadEnabled) {
        return (
            <div className="NoResult-Container Inventory-Unauth-Container">
                <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                <div className="NoResult-First-Title-Primary">You do not have permission to view this screen</div>
            </div>
        )
    }

    if (assetLoadLoading || assetInfoLoading) {
        return (
            <div className="EditEquipment-SpinnerContainer">
                <Loading />
            </div>
        )
    }

    return (
        <div className="EditEquipment-Container">
            <div className="EditEquipment-Header">
                <div className="EditEquipment-HeaderLabel">
                    Edit Load #: {loadId}
                </div>

                <div className="EditEquipment-HeaderButtonsContainer">
                    <Link
                        to={{
                            pathname: `${Routes.EQUIPMENT_MANAGEMENT.route}${loadId}`,
                        }}
                        className="EditEquipment-Cancel-Button"
                    >
                        <button className='CustomButton-Default CustomButton-Bordered EditEquipment-CancelButton'>
                            Cancel
                        </button>
                    </Link>

                    <CustomButton
                        title="Save"
                        onClick={() => { handleOnClickSave() }}
                        isDisabled={isSaveButtonDisabled() || mutationLoading}
                    />
                </div>
            </div>

            <div className="EditEquipment-DividerLine" />

            {
                mutationLoading && (
                    <div className="EditEquipment-SpinnerContainer">
                        <Loading />
                    </div>
                )
            }

            {
                !isContinueMode && <div className="EditEquipment-ErrorContainer">
                    {validationError}
                </div>
            }
            {
                assetLoadError && <div className="EditEquipment-ErrorContainer">
                    <Error error={assetLoadError} />
                </div>
            }
            {
                assetInfoError && <div className="EditEquipment-ErrorContainer">
                    <Error error={assetInfoError} />
                </div>
            }
            {
                mutationError &&
                <div className="EditEquipment-ErrorContainer">
                    <Error error={mutationError} />
                </div>
            }

            {!assetLoadError && !assetInfoError &&
                <div className="EditEquipment-BodyContainer">
                    <EquipmentDetailsEdit
                        assetStatus={assetLoadToSave?.status ?? ''}
                        assetInfo={assetInfo}
                        assetInfoToSave={assetInfoToSave}
                        setAssetInfoToSave={setAssetInfoToSave}
                        equipmentsInfo={equipmentsInfo}
                        updatePaycode={updatePaycode}
                        infoLoading={equipmentInfoDataLoading}
                    />
                    <LoadDriverDetailsEdit
                        assetInfoExists={assetInfoExists}
                        assetLoadToSave={assetLoadToSave}
                        setAssetLoadToSave={setAssetLoadToSave}
                        payCodeDropdownOptions={payCodeList}
                        selectedTrailer={assetLoadData?.getAssestLoadsByLoadId?.trailer ?? ''}
                    />
                </div>
            }

            {isContinueMode && (
                <Modal show={isContinueMode} onHide={() => setIsContinueMode(false)}>
                    <Modal.Header className="EditEquipment-ConfirmationModal-Head">
                        Out of Service
                        <i className="material-icons EditEquipment-ConfirmationModal-X-Button" onClick={() => setIsContinueMode(false)}>
                            close
                        </i>
                    </Modal.Header>
                    <Modal.Body>
                        {validationError}. Do you want to continue?
                    </Modal.Body>
                    <Modal.Footer>
                        <CustomButton title="Cancel" onClick={() => setIsContinueMode(false)} isBordered />
                        <CustomButton title="Continue" onClick={() => handleOnSuccessValidation()} isDisabled={mutationLoading} />
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default EditEquipmentDetails;

